import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import LeadFormButton from '../../../components/LeadFormButton';

const ProcessSteps = () => {
  const [activeStep, setActiveStep] = useState(0);

  const processSteps = [
    {
      id: 1,
      title: "Seleção da Madeira",
      subtitle: "Escolha Premium",
      description: `Nossa jornada começa com a seleção criteriosa de madeiras nobres brasileiras. Cada tora é inspecionada individualmente, considerando densidade, veios, cor natural e potencial de durabilidade.\n\nTrabalhamos exclusivamente com fornecedores certificados que seguem práticas sustentáveis de manejo florestal, garantindo que cada peça carregue não apenas beleza, mas também responsabilidade ambiental.`,
      icon: "TreePine",
      image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&h=600&fit=crop",
      details: [
        "Inspeção visual detalhada",
        "Verificação de certificação sustentável",
        "Seleção por projeto específico"
      ]
    },
    {
      id: 2,
      title: "Preparação e Secagem",
      subtitle: "Processo Controlado",
      description: `A madeira selecionada passa por um processo de secagem controlada em câmaras especiais, onde temperatura e umidade são monitoradas constantemente.\n\nEste processo, que pode levar semanas, é fundamental para garantir a estabilidade dimensional da madeira e prevenir rachaduras ou empenamentos futuros.`,
      icon: "Thermometer",
      image: "https://images.unsplash.com/photo-1504148455328-c376907d081c?w=800&h=600&fit=crop",
      details: [
        "Secagem em câmara controlada",
        "Aclimatação gradual"
      ]
    },
    {
      id: 3,
      title: "Design e Planejamento",
      subtitle: "Criação Personalizada",
      description: `Cada móvel é único e começa com um projeto personalizado. Nossos designers trabalham em estreita colaboração com o cliente para entender suas necessidades, estilo e o ambiente onde a peça será inserida.\n\nUtilizamos tecnologia 3D para visualização, mas mantemos o toque artesanal em cada detalhe do planejamento.`,
      icon: "PenTool",
      image: "https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=800&h=600&fit=crop",
      details: [
        "Consulta personalizada",
        "Modelagem 3D detalhada",
        "Seleção de acabamentos",
        "Aprovação do cliente"
      ]
    },
    {
      id: 4,
      title: "Corte e Modelagem",
      subtitle: "Precisão Artesanal",
      description: `Com o projeto aprovado, iniciamos o corte preciso da madeira. Utilizamos ferramentas tradicionais combinadas com equipamentos de alta precisão para garantir medidas exatas.\n\nCada corte é executado respeitando os veios naturais da madeira, maximizando sua beleza e resistência estrutural.`,
      icon: "Scissors",
      image: "https://images.unsplash.com/photo-1609205807107-e8ec2120f9de?w=800&h=600&fit=crop",
      details: [
        "Corte de precisão",
        "Respeito aos veios naturais",
        "Aproveitamento máximo da madeira"
      ]
    },
    {
      id: 5,
      title: "Montagem Artesanal",
      subtitle: "Técnicas Tradicionais",
      description: `A montagem é onde a magia acontece. Nossos artesãos utilizam técnicas centenárias de marcenaria, como encaixes tipo cauda de andorinha e espigas, garantindo união perfeita sem comprometer a integridade da madeira.\n\nCada junta é testada individualmente para garantir resistência e durabilidade excepcionais.`,
      icon: "Hammer",
      image: "https://images.unsplash.com/photo-1609205807107-e8ec2120f9de?w=800&h=600&fit=crop",
      details: [
        "Encaixes tradicionais",
        "Montagem sem pregos ou parafusos",
        "Ajustes de precisão"
      ]
    },
    {
      id: 6,
      title: "Acabamento Premium",
      subtitle: "Proteção e Beleza",
      description: `O acabamento é aplicado em múltiplas camadas, cada uma cuidadosamente lixada e polida. Utilizamos vernizes e óleos naturais que realçam a beleza da madeira enquanto oferecem proteção duradoura.\n\nO processo pode incluir técnicas especiais como pátina, envelhecimento ou texturização, dependendo do design escolhido.`,
      icon: "Paintbrush",
      image: "https://images.unsplash.com/photo-1609205807107-e8ec2120f9de?w=800&h=600&fit=crop",
      details: [
        "Múltiplas camadas de acabamento",
        "Produtos naturais premium",
        "Técnicas especiais sob demanda"
      ]
    }
  ];

  return (
    <section className="py-20 bg-canvas">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
              <Icon name="Settings" size={24} color="white" strokeWidth={2.5} />
            </div>
            <span className="font-inter text-primary font-semibold text-lg">Nosso Processo</span>
          </div>
          
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-6">
            Da Madeira Bruta ao
            <span className="block text-accent">Móvel dos Sonhos</span>
          </h2>
          
          <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Cada etapa do nosso processo artesanal é executada com precisão e paixão, garantindo que sua peça seja única e duradoura.
          </p>
        </div>

        {/* Process Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-border"></div>
          
          {/* Active Step Indicator */}
          <div 
            className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-primary rounded-full transition-all duration-500 z-10"
            style={{ top: `${(activeStep * 100) / (processSteps?.length - 1)}%` }}
          ></div>

          <div className="space-y-16">
            {processSteps?.map((step, index) => (
              <div
                key={step?.id}
                className={`relative transition-all duration-500 ${
                  index === activeStep ? 'opacity-100' : 'opacity-60 hover:opacity-80'
                }`}
                onMouseEnter={() => setActiveStep(index)}
              >
                <div className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}>
                  {/* Content */}
                  <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center shadow-lg">
                        <Icon name={step?.icon} size={28} color="white" strokeWidth={2.5} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-accent uppercase tracking-wide">
                          Etapa {step?.id}
                        </div>
                        <h3 className="font-playfair text-2xl font-bold text-primary">
                          {step?.title}
                        </h3>
                        <div className="text-lg text-text-secondary font-medium">
                          {step?.subtitle}
                        </div>
                      </div>
                    </div>

                    <div className="prose prose-lg max-w-none">
                      <p className="text-text-primary leading-relaxed whitespace-pre-line">
                        {step?.description}
                      </p>
                    </div>

                    {/* Details List */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-primary">Principais Atividades:</h4>
                      <ul className="space-y-2">
                        {step?.details?.map((detail, detailIndex) => (
                          <li key={detailIndex} className="flex items-center space-x-3">
                            <Icon name="Check" size={16} color="var(--color-success)" strokeWidth={2.5} />
                            <span className="text-text-secondary">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                  </div>

                  {/* Image */}
                  <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                      <Image
                        src={step?.image}
                        alt={`Processo: ${step?.title}`}
                        className="w-full h-96 object-cover"
                      />
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                      
                      {/* Step Number */}
                      <div className="absolute top-6 left-6 w-12 h-12 bg-accent rounded-full flex items-center justify-center">
                        <span className="font-bold text-accent-foreground text-lg">{step?.id}</span>
                      </div>
                    </div>

                    {/* Timeline Connector (Mobile) */}
                    <div className="lg:hidden absolute -bottom-8 left-1/2 transform -translate-x-1/2 w-1 h-16 bg-border"></div>
                  </div>
                </div>

                {/* Timeline Node (Desktop) */}
                <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-white border-4 border-primary rounded-full z-20"></div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-forest rounded-2xl p-12 text-white">
            <h3 className="font-playfair text-3xl font-bold mb-4">
              Quer Ver Nosso Processo em Ação?
            </h3>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Agende uma visita ao nosso ateliê e acompanhe de perto como criamos móveis únicos para você.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                className="bg-accent text-accent-foreground hover:bg-accent/90"
              >
                Agendar Visita ao Ateliê
              </Button>
              <LeadFormButton
                variant="outline"
                size="lg"
                iconName="MessageCircle"
                iconPosition="left"
                className="border-white text-white hover:bg-white hover:text-primary"
              >
                Falar no WhatsApp
              </LeadFormButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSteps;